import { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import {
  getOcppActiveSocketsForSiteOptions,
  getOcppConfigurationOptions,
  listOcppMessagesInfiniteOptions,
} from "../../../services/client/@tanstack/react-query.gen";
import type { OcppMessageView } from "../../../services/client/types.gen";
import {
  FETCH_PAGE_SIZE,
  useCursorPagination,
} from "../../../hooks/useCursorPagination";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import {
  MESSAGE_TIME_FORMAT,
  MESSAGE_TYPE_COLORS,
  MESSAGE_TYPE_LABELS,
  OCPP_ACTIONS,
  SENDER_LABELS,
  summarisePayload,
} from "./constants";
import { OcppMessageDetails } from "./OcppMessageDetails";
import { useOcppMessageFilters } from "./useOcppMessageFilters";
import { MessageRangeFilter } from "./MessageRangeFilter";

const columns: ColumnsType<OcppMessageView> = [
  {
    title: "Received",
    dataIndex: "timestamp",
    key: "timestamp",
    width: 170,
    render: (timestamp: string) => (
      <span className="whitespace-nowrap">
        {dayjs(timestamp).format(MESSAGE_TIME_FORMAT)}
      </span>
    ),
  },
  {
    title: "Charger",
    dataIndex: "socket_id",
    key: "socket_id",
    render: (socketId: string) => (
      <Typography.Text code className="whitespace-nowrap">
        {socketId}
      </Typography.Text>
    ),
  },
  {
    title: "Asset · connector",
    key: "asset",
    render: (_, message) =>
      message.connector_id === null || message.connector_id === undefined ? (
        <Typography.Text type="secondary">charger-wide</Typography.Text>
      ) : (
        <Space size={4} wrap={false}>
          {message.asset_id ? (
            <Tag>{message.asset_id}</Tag>
          ) : (
            <Tooltip title="This connector is not in the socket-to-asset map">
              <Tag color="orange">unmapped</Tag>
            </Tooltip>
          )}
          <span className="whitespace-nowrap">#{message.connector_id}</span>
        </Space>
      ),
  },
  {
    title: "Direction",
    dataIndex: "sender",
    key: "sender",
    render: (sender: OcppMessageView["sender"]) => (
      <span className="whitespace-nowrap">{SENDER_LABELS[sender]}</span>
    ),
  },
  {
    title: "Action",
    key: "action",
    render: (_, message) => (
      <Space size={4} wrap={false}>
        <Tag color={MESSAGE_TYPE_COLORS[message.message_type]}>
          {MESSAGE_TYPE_LABELS[message.message_type]}
        </Tag>
        <span className="whitespace-nowrap">{message.action ?? "—"}</span>
      </Space>
    ),
  },
  {
    title: "Details",
    key: "summary",
    render: (_, message) => {
      if (message.message_type === "call_error") {
        return (
          <Typography.Text type="danger">
            {message.error_code}: {message.error_description}
          </Typography.Text>
        );
      }
      if (message.message_type === "unparsable") {
        return (
          <Typography.Text type="secondary" ellipsis>
            {message.raw}
          </Typography.Text>
        );
      }
      return (
        <Space size={[4, 4]} wrap>
          {summarisePayload(message.payload).map(({ label, value }) => (
            <Tag key={label} bordered={false}>
              <Typography.Text type="secondary">{label}</Typography.Text>{" "}
              {value}
            </Tag>
          ))}
        </Space>
      );
    },
  },
];

type MessageFilters = ReturnType<typeof useOcppMessageFilters>;

// In the charger drawer the asset, direction and action filters sit in the
// column headers. They stay server-side: the selection lives in the URL.
const withHeaderFilters = (
  filters: MessageFilters,
  mappedAssets: string[],
): ColumnsType<OcppMessageView> =>
  columns
    .filter((column) => column.key !== "socket_id")
    .map((column) => {
      switch (column.key) {
        case "asset":
          return {
            ...column,
            filters: mappedAssets.map((assetId) => ({
              text: assetId,
              value: assetId,
            })),
            filteredValue: filters.assets,
          };
        case "sender":
          return {
            ...column,
            filters: (["charging_pole", "cpms"] as const).map((sender) => ({
              text: SENDER_LABELS[sender],
              value: sender,
            })),
            filterMultiple: false,
            filteredValue: filters.sender ? [filters.sender] : null,
          };
        case "action":
          return {
            ...column,
            filters: OCPP_ACTIONS.map((action) => ({
              text: action,
              value: action,
            })),
            filterSearch: true,
            filteredValue: filters.actions,
          };
        default:
          return column;
      }
    });

type OcppMessageLogProps = {
  site_id: string;
  // Shows only the messages of this charger, without a charger filter.
  socket_id?: string;
  // Prefix of the URL search params holding the filters.
  filterPrefix?: string;
};

export const OcppMessageLog = ({
  site_id,
  socket_id,
  filterPrefix,
}: OcppMessageLogProps) => {
  const filters = useOcppMessageFilters(filterPrefix);
  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => setSearchDraft(filters.search), [filters.search]);

  const { data: sockets } = useQuery({
    ...getOcppActiveSocketsForSiteOptions({ path: { site_id } }),
    enabled: !socket_id,
  });

  const { data: configuration } = useQuery({
    ...getOcppConfigurationOptions({ path: { site_id } }),
    retry: false,
  });
  const socketToAssetMap = configuration?.socket_to_asset_map ?? {};
  const mappedAssets = [
    ...new Set(
      socket_id
        ? (socketToAssetMap[socket_id] ?? [])
        : Object.values(socketToAssetMap).flat(),
    ),
  ].sort();
  const chargers = socket_id ? [socket_id] : filters.chargers;

  const {
    data,
    error,
    isLoading,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    ...listOcppMessagesInfiniteOptions({
      query: {
        site_id,
        page_size: FETCH_PAGE_SIZE,
        search: filters.search || null,
        socket_id: chargers.length > 0 ? chargers : null,
        asset_id: filters.assets.length > 0 ? filters.assets : null,
        action: filters.actions.length > 0 ? filters.actions : null,
        sender: filters.sender,
        received_after: filters.receivedRange.after,
        received_before: filters.receivedRange.before,
      },
    }),
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page ? lastPage.end_cursor : undefined,
  });

  const { dataSource, pagination, isPageLoading } =
    useCursorPagination<OcppMessageView>({
      pages: data?.pages,
      hasNextPage: hasNextPage ?? false,
      fetchNextPage,
      isFetchingNextPage,
    });

  const handleRefresh = () => {
    if (filters.preset) {
      filters.refresh();
    } else {
      void refetch();
    }
  };

  const handleTableChange: TableProps<OcppMessageView>["onChange"] = (
    _pagination,
    tableFilters,
    _sorter,
    { action },
  ) => {
    if (action !== "filter") {
      return;
    }
    const selected = (key: string) => (tableFilters[key] ?? []).map(String);
    const [sender] = selected("sender");
    filters.update({
      assets: selected("asset"),
      actions: selected("action"),
      sender: sender === "charging_pole" || sender === "cpms" ? sender : null,
    });
  };

  return (
    <div className="w-full">
      <Space wrap className="mb-4">
        <Input.Search
          allowClear
          className="w-72"
          placeholder="Badge, OCPP transaction id or message id"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onSearch={(value) => filters.update({ search: value.trim() })}
        />
        {!socket_id && (
          <>
            <Select<string[]>
              mode="multiple"
              allowClear
              className="min-w-48"
              placeholder="All chargers"
              options={(sockets ?? []).map((socket) => ({
                value: socket.id,
                label: socket.id,
              }))}
              value={filters.chargers}
              onChange={(chargers) => filters.update({ chargers })}
            />
            <Tooltip title="Requests on the connectors mapped to these assets, plus charger-wide requests (Heartbeat, BootNotification, OCPP 1.6 StopTransaction) from the same chargers. Responses are shown when you expand a row.">
              <Select<string[]>
                mode="multiple"
                allowClear
                className="min-w-48"
                placeholder="All assets"
                options={mappedAssets.map((assetId) => ({
                  value: assetId,
                  label: assetId,
                }))}
                value={filters.assets}
                onChange={(assets) => filters.update({ assets })}
              />
            </Tooltip>
            <Tooltip title="Filters requests by action. Their responses are shown when you expand a row.">
              <Select<string[]>
                mode="tags"
                allowClear
                className="min-w-56"
                placeholder="All actions"
                options={OCPP_ACTIONS.map((action) => ({
                  value: action,
                  label: action,
                }))}
                value={filters.actions}
                onChange={(actions) => filters.update({ actions })}
              />
            </Tooltip>
            <Select<OcppMessageView["sender"] | "all">
              className="w-40"
              options={[
                { value: "all", label: "Both directions" },
                { value: "charging_pole", label: SENDER_LABELS.charging_pole },
                { value: "cpms", label: SENDER_LABELS.cpms },
              ]}
              value={filters.sender ?? "all"}
              onChange={(sender) =>
                filters.update({ sender: sender === "all" ? null : sender })
              }
            />
          </>
        )}
        <MessageRangeFilter filters={filters} />
        <Button
          icon={<ReloadOutlined />}
          loading={isFetching && !isFetchingNextPage}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Space>

      {!!error && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Failed to load OCPP messages"
          description={extractErrorMessage(error)}
        />
      )}

      <Table<OcppMessageView>
        size="small"
        columns={socket_id ? withHeaderFilters(filters, mappedAssets) : columns}
        onChange={handleTableChange}
        dataSource={dataSource}
        loading={isLoading || isPageLoading}
        pagination={pagination}
        rowKey="id"
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (message) => (
            <OcppMessageDetails message={message} />
          ),
        }}
        locale={{
          emptyText:
            "No OCPP messages match these filters. Try a wider time range.",
        }}
      />
    </div>
  );
};
